/**
 * Hooks para buscar dados dos relatórios
 */
import { useQuery } from '@tanstack/react-query';
import { useRelatoriosFilters, type ImoveisScope } from './useRelatoriosFilters';
import { API_BASE_URL } from '@/lib/api';

const API_BASE = `${API_BASE_URL}/claro/api/reports`;

// ============================================================================
// TIPOS
// ============================================================================

/** KPIs de Imóveis */
export interface ImoveisKpis {
  total: number;
  validos: number;
  validosPercent: number;
  ambos: number;
  ambosPercent: number;
  andamento: number;
  andamentoPercent: number;
  mapeados: number;
  mapeadosPercent: number;
}

/** Agregação por tipo de unidade */
export interface ImoveisTypeLine {
  tipo: string;
  regular: number;
  andamento: number;
  irregular: number;
  total: number;
}

/** Linha unitária usada na exportação de lojas; o dashboard continua agregado por tipo. */
export interface ImoveisUnitLine {
  unitGuid: string;
  unidade: string;
  identificacao: string;
  tipo: string;
  subtipo: string;
  regional: string | null;
  uf: string | null;
  cidade: string | null;
  alvaraStatus: 'regular' | 'em_andamento' | 'irregular' | null;
  avcbStatus: 'regular' | 'em_andamento' | 'irregular' | null;
  classificacao: 'valido' | 'andamento' | 'mapeado';
}

/** Resposta de Imóveis */
export interface ImoveisResponse {
  scope: ImoveisScope;
  kpis: ImoveisKpis;
  byType: ImoveisTypeLine[];
  units?: ImoveisUnitLine[];
}

/** Resposta de Cenário (AVCB ou Alvarás) */
export interface CenarioResponse {
  regular: number;
  regularPercent: number;
  andamento: number;
  andamentoPercent: number;
  pendente: number;
  pendentePercent: number;
  total: number;
  regularAndamento: number;
  regularAndamentoPercent: number;
}

/** KPIs de Desempenho */
export interface DesempenhoKpis {
  totalFornecedores: number;
  totalProcessos: number;
  totalConcluidos: number;
  totalAtrasados: number;
  totalAndamento: number;
  totalDocumentos: number;
  mediaDocs: number;
}

/** Dados por tipo de documento de um fornecedor */
export interface SupplierDocType {
  type: string;
  concluido: number;
  andamento: number;
  atrasado: number;
  // Legacy
  regular: number;
  irregular: number;
}

/** Dados de um fornecedor */
export interface SupplierData {
  name: string;
  total: number;
  concluido: number;
  andamento: number;
  atrasado: number;
  // Legacy
  regular: number;
  irregular: number;
  byDocType: SupplierDocType[];
}

/** Resposta de Desempenho */
export interface DesempenhoResponse {
  kpis: DesempenhoKpis;
  bySupplier: SupplierData[];
  docTypes: string[];
  suppliers: string[];         // Lista de fornecedores disponíveis
  periods: string[];           // Períodos consultados (ex: "2025-10", "2025-11")
  availablePeriods: string[];  // Períodos disponíveis nos dados (para os filtros)
  statusIncluded: string[];    // Status incluídos na consulta
}

/** Informações de um snapshot */
export interface SnapshotInfo {
  filename: string;
  month: number;
  year: number;
  createdAt: string;
  unitsCount: number;
  documentsCount: number;
}

// ============================================================================
// HELPERS
// ============================================================================

interface ApiResponse<T> {
  status: string;
  timestamp: string;
  result: T;
}

async function fetchApi<T>(url: string): Promise<T> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }
  const json = await response.json() as ApiResponse<T>;
  return json.result;
}

function buildQueryString(params: Record<string, string | boolean | null | undefined>): string {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') {
      searchParams.set(key, String(value));
    }
  }
  const qs = searchParams.toString();
  return qs ? `?${qs}` : '';
}

// ============================================================================
// HOOKS - IMÓVEIS
// ============================================================================

/**
 * Hook para dados de Imóveis
 */
export function useImoveisData() {
  const { regional, uf, imoveisScope } = useRelatoriosFilters();
  
  return useQuery({
    queryKey: ['reports', 'imoveis', regional, uf, imoveisScope],
    queryFn: () => {
      const qs = buildQueryString({ regional, uf, scope: imoveisScope });
      return fetchApi<ImoveisResponse>(`${API_BASE}/imoveis${qs}`);
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
}

// ============================================================================
// HOOKS - CENÁRIO AVCB
// ============================================================================

/**
 * Hook para dados de Cenário AVCB
 */
export function useCenarioAvcb() {
  const { regional, uf } = useRelatoriosFilters();
  
  return useQuery({
    queryKey: ['reports', 'cenario-avcb', regional, uf],
    queryFn: () => {
      const qs = buildQueryString({ regional, uf });
      return fetchApi<CenarioResponse>(`${API_BASE}/cenario-avcb${qs}`);
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook para dados históricos de Cenário AVCB (de snapshots)
 */
export function useCenarioAvcbHistorico(month: number, year: number) {
  return useQuery({
    queryKey: ['reports', 'snapshot', 'cenario-avcb', month, year],
    queryFn: async () => {
      const snapshot = await fetchApi<{
        metadata: { month: number; year: number };
        units: unknown[];
        documents: unknown[];
      }>(`${API_BASE}/snapshots/${month}/${year}`);
      
      // Processa os dados do snapshot para calcular cenário AVCB
      // Isso é feito no cliente para snapshots, já que os dados já estão salvos
      return processSnapshotForCenario(snapshot, 'avcb');
    },
    staleTime: Infinity, // Snapshots são imutáveis
    enabled: month > 0 && year > 0,
  });
}

// ============================================================================
// HOOKS - CENÁRIO ALVARÁS
// ============================================================================

/**
 * Hook para dados de Cenário Alvarás
 */
export function useCenarioAlvaras() {
  const { regional, uf } = useRelatoriosFilters();
  
  return useQuery({
    queryKey: ['reports', 'cenario-alvaras', regional, uf],
    queryFn: () => {
      const qs = buildQueryString({ regional, uf });
      return fetchApi<CenarioResponse>(`${API_BASE}/cenario-alvaras${qs}`);
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook para dados históricos de Cenário Alvarás (de snapshots)
 */
export function useCenarioAlvarasHistorico(month: number, year: number) {
  return useQuery({
    queryKey: ['reports', 'snapshot', 'cenario-alvaras', month, year],
    queryFn: async () => {
      const snapshot = await fetchApi<{
        metadata: { month: number; year: number };
        units: unknown[];
        documents: unknown[];
      }>(`${API_BASE}/snapshots/${month}/${year}`);
      
      return processSnapshotForCenario(snapshot, 'alvara');
    },
    staleTime: Infinity,
    enabled: month > 0 && year > 0,
  });
}

// ============================================================================
// HOOKS - DESEMPENHO
// ============================================================================

/**
 * Hook para dados de Desempenho
 * Usa os filtros globais: regionals, years, months, docTypes, suppliers, supplierViewMode
 */
export function useDesempenho() {
  const { 
    selectedRegionals, 
    selectedYears, 
    selectedMonths, 
    selectedDocTypes,
    selectedSuppliers,
    selectedStatus,
    supplierViewMode 
  } = useRelatoriosFilters();
  
  // Converte Sets para arrays separados por vírgula
  const regionalsParam = selectedRegionals.size > 0 ? Array.from(selectedRegionals).join(',') : undefined;
  const yearsParam = selectedYears.size > 0 ? Array.from(selectedYears).join(',') : undefined;
  const monthsParam = selectedMonths.size > 0 ? Array.from(selectedMonths).join(',') : undefined;
  const docTypesParam = selectedDocTypes.size > 0 ? Array.from(selectedDocTypes).join(',') : undefined;
  const suppliersParam = selectedSuppliers.size > 0 ? Array.from(selectedSuppliers).join(',') : undefined;
  const statusParam = selectedStatus.size > 0 ? Array.from(selectedStatus).join(',') : undefined;
  
  // Traduz supplierViewMode para equipesOnly (backward compatibility com API)
  const equipesOnly = supplierViewMode === 'equipes';
  
  return useQuery({
    queryKey: ['reports', 'desempenho', regionalsParam, yearsParam, monthsParam, docTypesParam, suppliersParam, statusParam, equipesOnly],
    queryFn: () => {
      const qs = buildQueryString({ 
        regionals: regionalsParam, 
        years: yearsParam,
        months: monthsParam,
        docTypes: docTypesParam,
        suppliers: suppliersParam,
        status: statusParam,
        equipesOnly 
      });
      return fetchApi<DesempenhoResponse>(`${API_BASE}/desempenho${qs}`);
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// HOOKS - SNAPSHOTS
// ============================================================================

/**
 * Hook para listar snapshots disponíveis
 */
export function useSnapshots() {
  return useQuery({
    queryKey: ['reports', 'snapshots'],
    queryFn: () => fetchApi<SnapshotInfo[]>(`${API_BASE}/snapshots`),
    staleTime: 60 * 1000, // 1 minuto
  });
}

/**
 * Hook para obter um snapshot específico
 */
export function useSnapshot(month: number, year: number) {
  return useQuery({
    queryKey: ['reports', 'snapshot', month, year],
    queryFn: () => fetchApi<{
      metadata: { month: number; year: number; createdAt: string };
      units: unknown[];
      documents: unknown[];
    }>(`${API_BASE}/snapshots/${month}/${year}`),
    staleTime: Infinity,
    enabled: month > 0 && year > 0,
  });
}

// ============================================================================
// HELPERS - PROCESSAMENTO DE SNAPSHOTS
// ============================================================================

interface SnapshotDoc {
  UnitGuid?: string;
  DocumentTypeName?: string;
  DocumentType?: string;
  StatusName?: string;
}

interface SnapshotUnit {
  UnitGuid: string;
  UnitSituationName?: string;
  StatusUnitName?: string;
}

/**
 * Processa dados de um snapshot para calcular cenário AVCB ou Alvará
 */
function processSnapshotForCenario(
  snapshot: { units: unknown[]; documents: unknown[] },
  type: 'avcb' | 'alvara'
): CenarioResponse {
  const units = snapshot.units as SnapshotUnit[];
  const docs = snapshot.documents as SnapshotDoc[];
  
  // Filtra unidades válidas
  const validUnits = units.filter(
    (u) =>
      u.UnitSituationName !== 'Em prospecção' &&
      u.StatusUnitName !== 'Não configurada'
  );
  
  // Deduplica
  const seen = new Set<string>();
  const deduped = validUnits.filter((u) => {
    if (seen.has(u.UnitGuid)) return false;
    seen.add(u.UnitGuid);
    return true;
  });
  
  // Agrupa docs por unidade
  const docsByUnit = new Map<string, SnapshotDoc[]>();
  for (const doc of docs) {
    if (!doc.UnitGuid) continue;
    
    const typeName = (doc.DocumentTypeName || doc.DocumentType || '').toLowerCase();
    const isMatch = type === 'avcb'
      ? typeName.includes('avcb') || typeName.includes('clcb')
      : typeName.includes('alvara') && typeName.includes('funcionamento');
    
    if (!isMatch) continue;
    
    const arr = docsByUnit.get(doc.UnitGuid) || [];
    arr.push(doc);
    docsByUnit.set(doc.UnitGuid, arr);
  }
  
  // Classifica cada unidade
  let regular = 0;
  let andamento = 0;
  let pendente = 0;
  
  for (const unit of deduped) {
    const unitDocs = docsByUnit.get(unit.UnitGuid) || [];
    
    if (unitDocs.length === 0) {
      pendente++;
      continue;
    }
    
    // Aplica regra "pior prevalece"
    let hasIrregular = false;
    let hasAndamento = false;
    
    for (const doc of unitDocs) {
      const status = (doc.StatusName || '').toLowerCase();
      
      // Regular
      if (/^(isento|permanente|vigente)$/i.test(status) ||
          /^vigente\s*\/\s*em\s+renova/i.test(status)) {
        // É regular, não muda flags
      }
      // Em Andamento
      else if (/em (obten|renova)/i.test(status) ||
               /^vencido\s*\/\s*em\s+renova/i.test(status) ||
               /^inexistente\s*\/\s*em\s+obten/i.test(status)) {
        hasAndamento = true;
      }
      // Irregular
      else {
        hasIrregular = true;
      }
    }
    
    if (hasIrregular) pendente++;
    else if (hasAndamento) andamento++;
    else regular++;
  }
  
  const total = deduped.length;
  
  return {
    regular,
    regularPercent: total > 0 ? (regular / total) * 100 : 0,
    andamento,
    andamentoPercent: total > 0 ? (andamento / total) * 100 : 0,
    pendente,
    pendentePercent: total > 0 ? (pendente / total) * 100 : 0,
    total,
    regularAndamento: regular + andamento,
    regularAndamentoPercent: total > 0 ? ((regular + andamento) / total) * 100 : 0,
  };
}

// ============================================================================
// HOOKS - REGIONAIS (para filtros)
// ============================================================================

/**
 * Hook para buscar lista de regionais
 */
export function useRegionals() {
  return useQuery({
    queryKey: ['units', 'regionals'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/claro/api/units/regionals`);
      if (!response.ok) throw new Error('Failed to fetch regionals');
      const json = await response.json();
      // A API retorna result como array direto, não como objeto com .regionals
      return Array.isArray(json.result) ? json.result : json.result?.regionals || [];
    },
    staleTime: 10 * 60 * 1000,
  });
}
